import React from 'react'
import { ProductVariant } from '../../types/product'
import LoadingSpinner from '../LoadingSpinner'

interface ChooseVariantStepProps {
  variants: ProductVariant[]
  selectedVariantId: number | null
  onVariantSelect: (variant: ProductVariant) => void
  loading: boolean
}

const ChooseVariantStep: React.FC<ChooseVariantStepProps> = ({
  variants,
  selectedVariantId,
  onVariantSelect,
  loading
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (variants.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-6xl mb-4">🍽️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No variants available</h3>
        <p className="text-gray-500">This product doesn't have any active variants.</p>
      </div>
    )
  }

  const formatPrice = (priceCents: number) => {
    return `€${(priceCents / 100).toFixed(2)}`
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Choose Size/Format</h3>
        <p className="text-sm text-gray-500 mb-6">
          Select the size or format for your order
        </p>
      </div>

      <div className="grid gap-3">
        {variants.map((variant) => (
          <button
            key={variant.id}
            onClick={() => onVariantSelect(variant)}
            className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
              selectedVariantId === variant.id
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">{variant.name}</h4>
                {variant.sku && (
                  <p className="text-sm text-gray-500">SKU: {variant.sku}</p>
                )}
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-primary-600">
                  {formatPrice(variant.price_cents)}
                </div>
                {selectedVariantId === variant.id && (
                  <div className="text-xs text-primary-600 font-medium mt-1">
                    Selected
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {variants.length === 1 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-blue-400 mr-3">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-blue-800">Auto-selected</h4>
              <p className="text-sm text-blue-700">
                Only one variant available, automatically selected for you.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChooseVariantStep
